<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import { useControllableModel } from '../composables/useControllableModel';
import {
  COLLAPSIBLE_SECTION_CONTENT_FADE_IN_DELAY_MS,
  COLLAPSIBLE_SECTION_CONTENT_FADE_MS,
  COLLAPSIBLE_SECTION_HEIGHT_MS,
  type CollapsibleSectionProps,
} from './collapsible-section';

const CHEVRON_ICON_SIZE = 18;

const props = withDefaults(defineProps<CollapsibleSectionProps>(), {
  defaultOpen: false,
});

const openModel = defineModel<boolean>('open');

const { isOpen, toggle } = useControllableModel(
  openModel,
  () => props.defaultOpen,
);

/** Drives the height grow/shrink animation. */
const isExpanded = ref(false);

/** Drives the content fade in/out animation. */
const isContentVisible = ref(false);

/** When true, content fade-in waits for the height transition to finish. */
const delayContentFadeIn = ref(false);

/** Skip staggered transitions on the initial paint. */
const enableContentTransition = ref(false);

let closeContentFadeTimeout: ReturnType<typeof setTimeout> | null = null;

const clearCloseContentFadeTimeout = (): void => {
  if (closeContentFadeTimeout === null) {
    return;
  }

  clearTimeout(closeContentFadeTimeout);
  closeContentFadeTimeout = null;
};

const revealContentAfterExpand = (): void => {
  void nextTick(() => {
    if (isOpen.value) {
      isContentVisible.value = true;
    }
  });
};

const scheduleCollapseAfterContentFade = (): void => {
  clearCloseContentFadeTimeout();

  closeContentFadeTimeout = setTimeout(() => {
    closeContentFadeTimeout = null;

    if (!isOpen.value) {
      isExpanded.value = false;
    }
  }, COLLAPSIBLE_SECTION_CONTENT_FADE_MS);
};

watch(isOpen, (open) => {
  clearCloseContentFadeTimeout();

  if (open) {
    delayContentFadeIn.value = !isExpanded.value;
    isExpanded.value = true;
    isContentVisible.value = false;
    revealContentAfterExpand();
    return;
  }

  isContentVisible.value = false;

  if (!isExpanded.value) {
    return;
  }

  scheduleCollapseAfterContentFade();
});

onMounted(() => {
  if (isOpen.value) {
    isExpanded.value = true;
    isContentVisible.value = true;
  }

  requestAnimationFrame(() => {
    enableContentTransition.value = true;
  });
});

onUnmounted(clearCloseContentFadeTimeout);

const onHeaderKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggle();
  }
};

const heightTransitionStyle = computed(() => ({
  transitionDuration: `${String(COLLAPSIBLE_SECTION_HEIGHT_MS)}ms`,
}));

const contentTransitionStyle = computed(() => ({
  // Suppress the fade on the initial paint so an already-open section doesn't flash in.
  transitionDuration: enableContentTransition.value
    ? `${String(COLLAPSIBLE_SECTION_CONTENT_FADE_MS)}ms`
    : '0ms',
  transitionDelay:
    isContentVisible.value && delayContentFadeIn.value
      ? `${String(COLLAPSIBLE_SECTION_CONTENT_FADE_IN_DELAY_MS)}ms`
      : '0ms',
}));
</script>

<template>
  <section class="w-full space-y-2">
    <div
      role="button"
      tabindex="0"
      class="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white"
      :aria-expanded="isOpen"
      @click="toggle"
      @keydown="onHeaderKeydown"
    >
      <HugeIcon
        icon="chevron_down"
        :size="CHEVRON_ICON_SIZE"
        class="shrink-0 text-neutral-400 transition-transform duration-150"
        :class="{ '-rotate-90': !isOpen }"
      />
      <div class="min-w-0 flex-1">
        <slot name="title">
          <h2 class="truncate text-lg font-bold text-neutral-900">
            {{ props.title }}
            <span
              v-if="props.subtitle"
              class="ml-2 text-sm font-normal text-neutral-500"
            >
              {{ props.subtitle }}
            </span>
          </h2>
        </slot>
      </div>
      <div
        v-if="$slots.trailing"
        class="flex shrink-0 items-center self-center"
        @click.stop
      >
        <slot name="trailing" />
      </div>
    </div>

    <div
      class="grid transition-[grid-template-rows] ease-in-out"
      :class="isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
      :style="heightTransitionStyle"
    >
      <div class="overflow-hidden">
        <div
          class="transition-opacity ease-in-out"
          :class="isContentVisible ? 'opacity-100' : 'opacity-0'"
          :style="contentTransitionStyle"
        >
          <slot />
        </div>
      </div>
    </div>
  </section>
</template>
